import { StaffColor } from "../lib/staff-colors.ts";
import { decodeEntities } from "../lib/decode-entities.ts";

interface Appointment {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string;
  service_name: string;
  staff_id: string;
  staff_name: string;
}

interface StaffMember {
  id: string;
  name: string;
}

interface Props {
  appointments: Appointment[];
  staff: StaffMember[];
  staffColorMap: Map<string, StaffColor>;
  date: string; // YYYY-MM-DD
  onAction?: (id: string, status: string) => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

type StatusKey = "confirmed" | "tentative" | "completed" | "no_show" | "cancelled";

function statusStyle(status: string): { bg: string; text: string; label: string } {
  const map: Record<StatusKey, { bg: string; text: string; label: string }> = {
    confirmed: { bg: "var(--status-confirmed-bg)", text: "var(--status-confirmed)", label: "Confirmed" },
    tentative: { bg: "var(--status-tentative-bg)", text: "var(--status-tentative)", label: "Tentative" },
    completed: { bg: "var(--status-done-bg)", text: "var(--status-done)", label: "Complete" },
    no_show: { bg: "var(--status-noshow-bg)", text: "var(--status-noshow)", label: "No-show" },
    cancelled: { bg: "var(--status-cancelled-bg)", text: "var(--status-cancelled)", label: "Cancelled" },
  };
  return map[status as StatusKey] ?? { bg: "var(--paper-well)", text: "var(--ink-soft)", label: status };
}

export function CalendarListView({ appointments, staffColorMap, onAction }: Props) {
  const sorted = [...appointments].sort(
    (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime()
  );

  if (sorted.length === 0) {
    return (
      <div className="zd-empty">
        <h3>Nothing scheduled.</h3>
        <p>This day is clear. Use the day strip above to jump to another day, or click "+ New" to add a booking.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {sorted.map((appt) => {
        const color = staffColorMap.get(appt.staff_id);
        const sStyle = statusStyle(appt.status);
        const faded = appt.status === "completed" || appt.status === "no_show";

        return (
          <div
            key={appt.id}
            style={{
              background: "var(--paper-raised)",
              borderRadius: 14,
              padding: "16px 18px",
              boxShadow: "var(--shadow-sm)",
              borderLeft: `3px solid ${color?.border ?? "var(--accent)"}`,
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
              opacity: faded ? 0.7 : 1,
            }}
          >
            {/* Time */}
            <div
              style={{
                minWidth: 110,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 500,
                  fontSize: 18,
                  lineHeight: 1.2,
                  color: "var(--ink)",
                  fontFeatureSettings: "'tnum'",
                  letterSpacing: "-0.01em",
                }}
              >
                {formatTime(appt.start_at)}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--ink-faint)",
                  marginTop: 2,
                  fontFeatureSettings: "'tnum'",
                }}
              >
                → {formatTime(appt.end_at)}
              </div>
            </div>

            {/* Customer + service */}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14.5 }}>
                {decodeEntities(appt.customer_name)}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2 }}>
                {decodeEntities(appt.service_name)}
                {appt.staff_name && (
                  <>
                    <span style={{ color: "var(--ink-faint)", margin: "0 6px" }}>·</span>
                    {appt.staff_name}
                  </>
                )}
              </div>
            </div>

            {/* Status badge */}
            <span
              className="badge"
              style={{
                background: sStyle.bg,
                color: sStyle.text,
                flexShrink: 0,
                fontSize: 11.5,
              }}
            >
              {sStyle.label}
            </span>

            {/* Action buttons */}
            {onAction && appt.status === "confirmed" && (
              <div style={{ display: "inline-flex", gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => onAction(appt.id, "completed")}
                  className="zd-ghost-btn primary"
                >
                  Mark complete
                </button>
                <button
                  onClick={() => onAction(appt.id, "no_show")}
                  className="zd-ghost-btn"
                >
                  No-show
                </button>
                <button
                  onClick={() => onAction(appt.id, "cancelled")}
                  className="zd-ghost-btn"
                  style={{ color: "var(--status-cancelled)" }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
